import { downloadCSV, escapeForCSV } from '../../../../core/utils/download.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { convertArrayToCsv } from '../../../utils/download-utils.js';
import type {
  Cell,
  Column,
  Header,
  Row,
  RowModel,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * DataTableV2 props extensions for the download feature
 * @public
 */
export interface DataTableV2DownloadProps {
  /**
   * Called when table data was downloaded (programmatically or via the toolbar).
   * @param subset - indicates which data was downloaded (all data, current page, selected rows)
   */
  onDownloadData?: (subset: 'all' | 'page' | 'selected') => void;
}

/**
 * Extension interface for the TanStack table instance.
 * @internal
 */
export interface DataTableV2DownloadDataInstance {
  downloadAllData: () => void;

  downloadPageData: () => void;

  downloadSelectedData: () => void;
}

/**
 * Extension interface for the TanStack table instance.
 * @internal
 */
export interface DataTableV2DownloadDataHeader {
  _getDownloadString: () => string;
}

/**
 * Extension interface for the TanStack table instance.
 * @internal
 */
export interface DataTableV2DownloadDataCell {
  _getDownloadString: () => string;
}

export interface DataTableV2DownloadDataOptions {
  onDownloadData?: DataTableV2DownloadProps['onDownloadData'];
}

/**
 * DataTableV2 slot definition for the Download action
 * @public
 */
export function DataTableV2Download() {
  return null;
}

/**
 * Default name of the downloaded CSV file.
 */
const CSV_FILE_NAME = 'table-data.csv';

/**
 * Column header that is prepended to the CSV file, if the table has sub-rows.
 * Indicates the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 */
const INDEX_COLUMN_HEADER = 'index';

/**
 * Extracts the column string representation for the download CSV.
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param includeIndexColumnHeader - if true, prepends an `index` column,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 */
function getCsvHeaders<TData extends DataTableV2RowData>(
  table: Table<TData>,
  includeIndexColumnHeader: boolean,
): string[] {
  const columnHeaders = table
    .getLeafHeaders()
    .filter((header) => {
      // If there are subheaders, we can also skip here. We work through
      // the remaining leaves later.
      if (header.subHeaders.length > 0) {
        return false;
      }

      // Don't include placeholder headers
      if (header.isPlaceholder) {
        return false;
      }
      // Don't include built in columns in the download info
      if (header.column.columnDef.meta?.builtin === true) {
        return false;
      }
      if (header.column.getIsVisible() === false) {
        return false;
      }
      return true;
    })
    .map((header) => header._getDownloadString());

  if (includeIndexColumnHeader) {
    columnHeaders.unshift(INDEX_COLUMN_HEADER);
  }
  return columnHeaders;
}

/**
 * @returns all visible cells of the given row as downloadable string.
 */
function getCsvCells<TData extends DataTableV2RowData>(
  row: Row<TData>,
): string[] {
  return row
    .getVisibleCells()
    .filter((cell) => cell.column.columnDef.meta?.builtin !== true)
    .map((cell) => cell._getDownloadString());
}

/**
 * @returns the given row's 1-based index, indicating its indentation level.
 * - Root rows have indices 1, 2, 3, 4, ...
 * - 2nd level sub-rows rows have indices 1.1, 1.2, 1.3, 2.1, 2.2, ...
 * - 3rd level sub-rows rows have indices 1.1.1, 1.1.2, 1.2.1, 1.3.1, 2.1.1, 2.1.2, ...
 * - etc.
 */
function getRowIndex<TData extends DataTableV2RowData>(row: Row<TData>) {
  let indexPrefix = '';
  for (const parent of row.getParentRows()) {
    indexPrefix = `${indexPrefix}${parent.index + 1}.`;
  }
  return `${indexPrefix}${row.index + 1}`;
}

/**
 * Extracts the headers and cell data string representation for the download CSV.
 * Utilizes async batching intended for large amounts of rows (e.g. 100k)
 * to unblock the main thread.
 *
 * If the table has sub-rows,  an `index` column is prepended,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param rowModel - the rows to process.
 */
async function getCsv<TData extends DataTableV2RowData>(
  table: Table<TData>,
  rowModel: RowModel<TData>,
): Promise<string[][]> {
  const nrOfRows = rowModel.flatRows.length;
  const csvData: string[][] = Array.from({ length: nrOfRows + 1 }); // rows + header
  const batchSize = 1_000;
  let currentIndex = 0;
  const includeIndexColumn = table.getHasSubRows();
  csvData[0] = getCsvHeaders(table, includeIndexColumn);

  function processBatch(): Promise<void> {
    return new Promise((resolve) => {
      const currentBatchEnd = Math.min(currentIndex + batchSize, nrOfRows);
      for (let i = currentIndex; i < currentBatchEnd; i++) {
        const row = rowModel.flatRows[i];
        const csvRow = getCsvCells(row);
        if (includeIndexColumn) {
          const rowIndex = getRowIndex(row);
          csvRow.unshift(rowIndex);
        }
        csvData[i + 1] = csvRow;
      }
      currentIndex = currentBatchEnd;

      if (currentIndex < nrOfRows) {
        // yield control to the browser before continuing with next batch
        setTimeout(() => resolve(processBatch()), 0);
      } else {
        resolve();
      }
    });
  }

  await processBatch();
  return csvData;
}

function downloadData<TData extends DataTableV2RowData>(
  table: Table<TData>,
  rowModel: RowModel<TData>,
  subset: 'all' | 'page' | 'selected',
) {
  getCsv(table, rowModel).then((rows) => {
    const csvString = convertArrayToCsv(rows);
    downloadCSV(csvString, CSV_FILE_NAME);
    table.options.onDownloadData?.(subset);
  });
}

export const DataTableV2DownloadData: TableFeature = {
  createHeader<TData extends DataTableV2RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header._getDownloadString = () => {
      const nestedHeaderNames: string[] = [];

      let currentColumnNesting: Column<TData, TValue> | undefined =
        header.column;

      // Go up the parent chain and find the correct names for the download names.
      while (currentColumnNesting !== undefined) {
        const columnDefHeader = currentColumnNesting.columnDef.header;
        // If the column definition defines either a custom element or nothing,
        // we call back to the header.id defined in the column def.
        if (
          typeof columnDefHeader === 'function' ||
          columnDefHeader === undefined
        ) {
          nestedHeaderNames.push(header.id);
        } else {
          nestedHeaderNames.push(columnDefHeader);
        }
        // Go up the nesting chain.
        currentColumnNesting = currentColumnNesting.parent;
      }

      return nestedHeaderNames.toReversed().join('.');
    };
  },

  createCell<TData extends DataTableV2RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData>,
    row: Row<TData>,
    table: Table<TData>,
  ) {
    cell._getDownloadString = () => {
      const cellValue = cell.getValue();

      // If there is no cell value, return an empty string.
      if (cellValue === undefined || cellValue === null) {
        return '';
      }

      // If the cell value is a Date object, return the iso string.
      if (cellValue instanceof Date) {
        return cellValue.toISOString();
      }

      // If the cell is in a columnType 'date', we should try to move it to a `Date` and export the iso string.
      if (
        column.columnDef.columnType === 'date' &&
        (typeof cellValue === 'string' || typeof cellValue === 'number')
      ) {
        // First try to actually parse it directly into a Date.
        // That should work for numbers and strings that have a correct date format.
        const cellDate = new Date(cellValue);
        if (!isNaN(cellDate.getTime())) {
          return cellDate.toISOString();
        }

        // Check for stringified timestamp values next.
        if (typeof cellValue === 'string' && !isNaN(parseInt(cellValue, 10))) {
          const cellDate = new Date(parseInt(cellValue, 10));
          if (!isNaN(cellDate.getTime())) {
            return cellDate.toISOString();
          }
        }

        // Unparsable string or number in the `columnType=date` column.
        // Fall through to the remaining processing steps.
      }

      // If the cell value is of type string or is an array, return an escaped version of the value.
      if (typeof cellValue === 'string' || Array.isArray(cellValue)) {
        return escapeForCSV(cellValue);
      }

      // If the cell value is an object
      if (typeof cellValue === 'object' && cellValue !== null) {
        // if the object has a `toString` function defined, respect it
        if (
          Object.prototype.hasOwnProperty.call(cellValue, 'toString') &&
          typeof cellValue.toString === 'function'
        ) {
          return escapeForCSV(cellValue.toString());
        }

        try {
          return escapeForCSV(JSON.stringify(cellValue));
        } catch {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.warn(
              `Accessed cell value for column "${column.id}" received an object, but was not stringifyable. Consider providing a toString() function on the object.`,
            );
          }
        }
      }

      return `${cellValue}`;
    };
  },

  createTable<TData extends DataTableV2RowData>(table: Table<TData>) {
    /**
     * Allows to download the entire table as a csv file.
     * Will only include the visible columns.
     */
    table.downloadAllData = () =>
      downloadData(table, table.getCoreRowModel(), 'all');

    /**
     * Allows to download the current page as a csv file.
     * Will only include the visible columns.
     */
    table.downloadPageData = () =>
      downloadData(table, table.getPaginationRowModel(), 'page');

    /**
     * Allows to download the current selected rows as a csv file.
     * Will only include the visible columns.
     */
    table.downloadSelectedData = () =>
      downloadData(table, table.getSelectedRowModel(), 'selected');
  },
};

/**
 * Configuration hook for download of the DataTableV2.
 * @internal
 */
export function useDownloadData<TData extends DataTableV2RowData>(
  props: DataTableV2DownloadProps,
  options: TableOptions<TData>,
) {
  options.onDownloadData = props.onDownloadData;
}
